/**
 * @file ClientSettingAdditionalUsers.js
 * @description Component to view and edit the "Enable Additional User Accounts" client option.
 */

import { useState, useEffect } from 'react';
import {
    getClientOptions,
    createClientOption,
    updateClientOption,
} from '../../services/clientService';
import { getConfigData } from '../../services/authService';
import Toast from '../ui/toasts/Toast';
import { SecurityAttributes, ClientOptionEnum } from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { getText } from '../../i18n';

const normalizeBoolString = v =>
    String(v).toLowerCase() === 'true' ? 'true' : 'false';

const ClientSettingAdditionalUsers = () => {
    const [
        initialEnableAdditionalUsersSetting,
        setInitialEnableAdditionalUsersSetting,
    ] = useState(null);

    // Store as string to match existing API contract in this codebase.
    const [selectedEnableAdditionalUsers, setSelectedEnableAdditionalUsers] =
        useState('false');

    const [isOpen, setIsOpen] = useState(true);
    const { setCompanyConfig, hasAttribute } = useAuthContext();

    const getOptions = async () => {
        try {
            const data = await getClientOptions(0, [
                ClientOptionEnum.EnableAdditionalUserAccounts,
            ]);

            const optionObject = (data || []).find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.EnableAdditionalUserAccounts,
            );

            if (optionObject) {
                setInitialEnableAdditionalUsersSetting(optionObject);
                setSelectedEnableAdditionalUsers(
                    normalizeBoolString(optionObject.value),
                );
            } else {
                // Default for UI display: OFF (false) when no explicit option exists.
                setInitialEnableAdditionalUsersSetting({
                    clientOptionId: ClientOptionEnum.EnableAdditionalUserAccounts,
                    value: 'false',
                    usingDefaultValue: true,
                });
                setSelectedEnableAdditionalUsers('false');
            }
        } catch (error) {
            console.error('Could not fetch:', error);
            Toast.error(
                getText('msg_cebfe17181c9'),
            );
        }
    };

    useEffect(() => {
        getOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleAccordion = () => {
        setIsOpen(prev => !prev);
    };

    const handleSave = async () => {
        if (!initialEnableAdditionalUsersSetting) return;

        const clientOptionId =
            initialEnableAdditionalUsersSetting.clientOptionId ??
            initialEnableAdditionalUsersSetting.id ??
            ClientOptionEnum.EnableAdditionalUserAccounts;

        const option = {
            clientOptionId,
            value: normalizeBoolString(selectedEnableAdditionalUsers),
        };

        let response;
        let didChange = false;

        if (initialEnableAdditionalUsersSetting.usingDefaultValue) {
            didChange = true;
            response = await createClientOption(option, 0);

            if (response?.error) {
                Toast.error(
                    getText('msg_1ad5844b3de9'),
                );
                return;
            }

            setInitialEnableAdditionalUsersSetting(response.data);
        } else if (
            normalizeBoolString(initialEnableAdditionalUsersSetting.value) !==
            normalizeBoolString(selectedEnableAdditionalUsers)
        ) {
            didChange = true;
            response = await updateClientOption(option, 0);

            if (response?.error) {
                Toast.error(
                    getText('msg_037736cb8051'),
                );
                return;
            }

            setInitialEnableAdditionalUsersSetting(response.data);
        }

        if (!didChange) {
            Toast.warning(getText('msg_13c3a7ac4e47'));
            return;
        }

        Toast.success(getText('msg_a3d10ada7088'));

        const companyCode = localStorage.getItem('companyCode');
        const updatedConfig = await getConfigData(companyCode);
        setCompanyConfig(updatedConfig);
    };

    const handleCancel = () => {
        setSelectedEnableAdditionalUsers(
            normalizeBoolString(initialEnableAdditionalUsersSetting?.value),
        );
    };

    return (
        <div
            style={{
                overflow: 'hidden',
                borderRadius: '12px',
                border: 'solid 1px rgba(47, 43, 67, 0.2)',
                marginTop: '10px',
                boxShadow:
                    'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px',
            }}
        >
            <div
                style={{
                    overflowY: 'auto',
                    padding: '16px',
                    resize: 'none',
                    boxShadow:
                        'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px',
                }}
            >
                <div>
                    <div style={{ marginLeft: '12px' }}>
                        {getText('msg_69bde34bdd4d')}
                        <i
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color3)',
                                display: 'flex',
                                float: 'inline-end',
                                cursor: 'pointer',
                            }}
                            className={
                                'bi bi-chevron-' + (isOpen ? 'up' : 'down')
                            }
                            onClick={toggleAccordion}
                        />
                    </div>
                </div>

                {isOpen && (
                    <div
                        style={{
                            marginLeft: '48px',
                            marginRight: '48px',
                            marginTop: '12px',
                            color: 'var(--iNGAGED-Neutral-Color2)',
                        }}
                    >
                        <div style={{ marginTop: '12px' }}>
                            <h6 className="item-page-name-static">{getText('msg_2f81a22de0af')}</h6>
                            <p>
                                {getText('msg_7b032b78bdb9')}
                            </p>
                        </div>

                        <div style={{ marginLeft: '24px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'anchor-center',
                                    marginTop: '12px',
                                }}
                            >
                                <div className="form-switch">
                                    <input
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={
                                            normalizeBoolString(
                                                selectedEnableAdditionalUsers,
                                            ) === 'true'
                                        }
                                        onChange={e =>
                                            setSelectedEnableAdditionalUsers(
                                                e.target.checked
                                                    ? 'true'
                                                    : 'false',
                                            )
                                        }
                                    />
                                </div>
                                <div>{getText('msg_56d3e8857f74')}</div>
                            </div>
                        </div>
                        {hasAttribute(
                            SecurityAttributes.ClientSettingsFullAccess
                        ) && (
                                <div
                                    className={`d-flex justify-content-evenly mt-5`}
                                >
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="ing-popup-blue-negative"
                                    >
                                        {getText('msg_77dfd2135f4d')}
                                    </button>
                                    <button
                                        type="button"
                                        className="ing-button-primary-none"
                                        onClick={handleSave}
                                    >
                                        {getText('msg_efc007a393f6')}
                                    </button>
                                </div>
                            )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientSettingAdditionalUsers;
